import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { LockKey, Warning } from "@phosphor-icons/react";
import { toast } from "sonner";
import { formatClockTime, formatWorkDate, COMPANY_TZ_LABEL } from "@/lib/time";

/** Closes ONE open shift, addressed by its record id.
 *  Never sends technician_id, so an employee's other (valid) shift is untouched.
 */
export const CloseShiftDialog = ({ entry, open, onOpenChange, onClosed }) => {
    const [clockOut, setClockOut] = useState("");
    const [subId, setSubId] = useState("");
    const [subs, setSubs] = useState([]);
    const [reason, setReason] = useState("");
    const [notes, setNotes] = useState("");
    const [saving, setSaving] = useState(false);

    useEffect(() => {
        if (!open || !entry) return;
        setReason("");
        setNotes(entry.notes || "");
        setSubId(entry.sublocation_id || "");
        // Default the clock out to the shift's own day at 5:00 PM if it started
        // long ago, otherwise "now" — the admin always confirms the value.
        const now = new Date();
        const pad = (n) => String(n).padStart(2, "0");
        const localNow = `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
        setClockOut(entry.needs_review ? `${entry.work_date}T17:00` : localNow);

        if (entry.requires_sublocation) {
            api.get(`/sites/${entry.site_id}/sublocations?active_only=true`)
                .then(({ data }) => setSubs(Array.isArray(data) ? data : []))
                .catch(() => setSubs([]));
        } else {
            setSubs([]);
        }
    }, [open, entry]);

    const submit = async () => {
        if (saving || !entry) return;
        if (reason.trim().length < 3) {
            toast.error("Please enter a correction reason");
            return;
        }
        if (entry.requires_sublocation && !subId) {
            toast.error("Select the Data Hall this shift was worked in");
            return;
        }
        setSaving(true);
        try {
            const { data } = await api.post(`/time/entries/${entry.id}/close`, {
                clock_out_local: clockOut,
                sublocation_id: entry.requires_sublocation || subId ? subId || null : null,
                reason: reason.trim(),
                notes,
            });
            toast.success(`Shift closed — ${data.elapsed_display}`, {
                description: "Logged in the audit trail",
            });
            onOpenChange(false);
            onClosed?.(data);
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
        } finally {
            setSaving(false);
        }
    };

    return (
        <Dialog open={open} onOpenChange={(o) => !saving && onOpenChange(o)}>
            <DialogContent className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="close-shift-dialog">
                <DialogHeader>
                    <DialogTitle className="text-[#003366]" style={{ fontFamily: "Outfit" }}>
                        Close this shift
                    </DialogTitle>
                    <DialogDescription>
                        {entry?.technician_name} · {formatWorkDate(entry?.work_date)} · {entry?.site_name}
                        {" · in "}{formatClockTime(entry?.clock_in_local)}
                    </DialogDescription>
                </DialogHeader>

                {entry && (
                    <div className="space-y-4">
                        <div className="rounded-lg border border-amber-300 bg-amber-50 px-3 py-2.5 text-xs text-amber-800 flex gap-2"
                            data-testid="close-shift-scope-note">
                            <Warning size={16} weight="fill" className="shrink-0 mt-0.5" />
                            <span>
                                Only this shift (record <span className="font-mono">{entry.id.slice(0, 8)}</span>) is
                                affected. Any other open shift for {entry.technician_name || "this employee"} stays exactly as it is.
                            </span>
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Clock Out ({COMPANY_TZ_LABEL}) <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Input
                                type="datetime-local"
                                value={clockOut}
                                onChange={(e) => setClockOut(e.target.value)}
                                className="h-11"
                                data-testid="close-shift-clock-out-input"
                            />
                        </div>

                        {entry.requires_sublocation && (
                            <div className="space-y-2">
                                <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                    Data Hall / Sub-location <span className="text-[#E6002C]">*</span>
                                </Label>
                                <Select value={subId} onValueChange={setSubId}>
                                    <SelectTrigger className="h-11" data-testid="close-shift-sublocation-select">
                                        <SelectValue placeholder="Where was this worked?" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        {subs.map((s) => (
                                            <SelectItem key={s.id} value={s.id}>
                                                {s.name}{s.job_code ? ` · ${s.job_code}` : ""}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                        )}

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Correction reason <span className="text-[#E6002C]">*</span>
                            </Label>
                            <Input
                                value={reason}
                                onChange={(e) => setReason(e.target.value)}
                                placeholder="e.g. Employee forgot to clock out"
                                className="h-11"
                                data-testid="close-shift-reason-input"
                            />
                        </div>

                        <div className="space-y-2">
                            <Label className="text-xs uppercase tracking-wider font-semibold text-[#475569]">
                                Notes (optional)
                            </Label>
                            <Textarea
                                value={notes}
                                onChange={(e) => setNotes(e.target.value)}
                                className="min-h-[64px]"
                                data-testid="close-shift-notes-input"
                            />
                        </div>
                    </div>
                )}

                <DialogFooter className="gap-2 sm:gap-0">
                    <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}
                        data-testid="close-shift-cancel">
                        Cancel
                    </Button>
                    <Button onClick={submit} disabled={saving}
                        className="bg-[#0066D9] hover:bg-[#0052ad] text-white font-semibold"
                        data-testid="close-shift-confirm">
                        <LockKey size={16} className="mr-1.5" />
                        {saving ? "Closing…" : "Close shift"}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
};
